<template>
  <div class="min-h-screen bg-growth-10">
    <template v-if="engagement">
      <ContextBar
        v-if="engagement.entity"
        class="!border-growth-50 !bg-white !px-10 !py-2 max-md:!px-5"
      >
        <font-awesome-icon :icon="entityIcon" class="h-3.5 w-3.5" />
        <span
          v-if="engagement.entity_type_name"
          class="font-semibold text-growth-800"
          >{{ engagement.entity_type_name }}:</span
        >
        <CC_Router_Link_To_Entity
          :params="{
            data: { id: engagement.entity, name: engagement.entity_name },
            hideIcon: true,
          }"
        />
      </ContextBar>

      <PageHeader
        class="engagement-page-header !border-growth-50"
        :title="pageHeaderTitle"
        :breadcrumbs="pageHeaderBreadcrumbs"
        title-class="sensitive"
      >
        <template #actions>
          <SalesforceLink :record="engagement.salesforce" label="Open in Salesforce" />
          <div class="secondary-picker">
            <TeamsMeetingSelector
              :engagement-id="engagement.id"
              :engagement-date="engagement.date"
            />
          </div>
        </template>
      </PageHeader>

      <main class="space-y-5 p-5 md:p-10">
        <section class="card">
          <ComponentHeader title="Details" :icon="['fas', 'info-circle']">
            <span
              v-if="lastAutoSaveTime"
              class="flex items-center gap-1.5 text-xs text-growth-600"
            >
              <font-awesome-icon
                :icon="['fas', 'circle-check']"
                class="text-green-dark"
              />Auto-saved {{ lastAutoSaveTime }}
            </span>
          </ComponentHeader>
          <div class="space-y-4 p-5">
            <div class="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
              <div class="col-span-2">
                <label class="field-label"
                  >Partner <span class="text-red-dark">*</span></label
                >
                <div v-if="!engagement.entity" class="secondary-picker mt-1">
                  <EntitySearch_Single
                    buttonText="Select Entity"
                    modalTitle="Select Entity"
                    @entity-selected="onEntitySelected"
                  />
                </div>
                <div
                  v-else
                  class="partner-box mt-1 flex h-[34px] items-center justify-between gap-3 rounded-lg border border-gray-300 bg-white px-3"
                >
                  <router-link
                    :to="{
                      name: 'Entity',
                      params: { entity_id: engagement.entity },
                    }"
                    class="sensitive min-w-0 truncate text-sm font-semibold text-growth-900 hover:underline"
                  >
                    {{ engagement.entity_name }}
                  </router-link>
                  <EntitySearch_Single
                    buttonText="Change"
                    modalTitle="Select New Entity"
                    @entity-selected="onEntitySelected"
                  />
                </div>
              </div>
              <div>
                <label class="field-label">Type</label>
                <select
                  v-model="engagement.engagement_type"
                  class="field-input mt-1"
                >
                  <option
                    v-for="type in engagement_types"
                    :key="type.id"
                    :value="type.id"
                  >
                    {{ type.name }}
                  </option>
                </select>
              </div>
              <div>
                <label class="field-label">Channel</label>
                <select
                  v-model="engagement.engagement_channel"
                  class="field-input mt-1"
                >
                  <option
                    v-for="type in filteredEngagementChannels"
                    :key="type.id"
                    :value="type.id"
                  >
                    {{ type.name }}
                  </option>
                </select>
              </div>
              <div>
                <label class="field-label">Status</label>
                <select v-model="engagement.status" class="field-input mt-1">
                  <option
                    v-for="status in engagement_statuses"
                    :key="status.id"
                    :value="status.id"
                  >
                    {{ status.name }}
                  </option>
                </select>
              </div>
              <div>
                <label class="field-label">Date</label>
                <input
                  v-model="engagement.date"
                  type="date"
                  class="field-input mt-1"
                />
              </div>
              <div>
                <label class="field-label">Owner</label>
                <div
                  class="mt-1 flex h-[34px] items-center gap-1.5 rounded-lg border border-gray-300 bg-gray-50 px-3 text-sm text-growth-900"
                >
                  <span class="sensitive min-w-0 truncate" data-spoof-kind="person">{{
                    engagement.user_name || '—'
                  }}</span>
                  <span
                    v-if="!engagement.virtual_meeting_uuid"
                    class="whitespace-nowrap text-xs text-gray-500"
                    >(manual upload)</span
                  >
                  <span
                    v-else-if="engagement.salesforce"
                    class="whitespace-nowrap text-xs text-gray-500"
                    >(Salesforce {{ engagement.salesforce.kind }} owner)</span
                  >
                </div>
              </div>
            </div>
            <EngagementTopicSelector
              v-if="
                engagement_topics.length &&
                engagement.engagement_topics !== undefined
              "
              v-model="engagement.engagement_topics"
              :topics="engagement_topics"
              class="topic-selector"
            />
            <div>
              <label class="field-label">Subject</label>
              <input
                v-model="engagement.subject"
                placeholder="Enter engagement subject..."
                class="sensitive field-input mt-1"
              />
            </div>
            <div>
              <label class="field-label">Notes</label>
              <textarea
                v-model="engagement.note"
                rows="5"
                placeholder="Enter engagement notes..."
                class="sensitive field-input mt-1 min-h-[120px] resize-y leading-6"
              ></textarea>
            </div>
            <div
              v-if="engagement_field_errors.length"
              class="rounded-lg border border-red-light bg-red-tint p-4"
            >
              <Field_Errors :received_errors="engagement_field_errors" />
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <button type="button" class="btn-primary-sm" @click="updateEngagement">
                <font-awesome-icon :icon="['fas', 'floppy-disk']" />Save Changes
              </button>
              <button
                type="button"
                class="btn-secondary-sm"
                @click.prevent="discardEngagement"
              >
                <font-awesome-icon :icon="['fas', 'xmark']" />Discard
              </button>
            </div>
          </div>
        </section>

        <section
          v-if="engagement.parsed_engagement_structure?.key_meeting_takeaway"
          class="flex gap-3 rounded-lg border border-secondary-200 bg-secondary-100 p-5"
        >
          <font-awesome-icon
            :icon="['fas', 'lightbulb']"
            class="mt-0.5 h-4 w-4 shrink-0 text-green-dark"
          />
          <div>
            <h2 class="text-sm font-semibold text-growth-900">Key Takeaway</h2>
            <p class="mt-1 text-sm leading-6 text-growth-800">{{ engagement.parsed_engagement_structure.key_meeting_takeaway }}</p>
          </div>
        </section>

        <section class="card">
          <ComponentHeader title="Transcript" :icon="['fas', 'file-lines']">
            <template v-if="engagement.zoomMeetingTranscription">
              <button
                type="button"
                class="btn-secondary-sm"
                @click="transcriptModalOpen = true"
              >
                <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" />Open transcript
              </button>
              <button
                type="button"
                class="text-sm font-semibold text-red-dark hover:underline"
                @click="replaceTranscriptModalOpen = true"
              >
                Replace
              </button>
            </template>
            <template v-else>
              <button
                v-if="isTenantVytalize"
                type="button"
                class="btn-secondary-sm"
                :disabled="fetchingTranscript"
                @click="fetchTranscript"
              >
                <font-awesome-icon
                  :icon="['fas', fetchingTranscript ? 'spinner' : 'download']"
                  :class="{ 'fa-spin': fetchingTranscript }"
                />{{ fetchingTranscript ? 'Searching...' : 'Search Microsoft' }}
              </button>
              <button
                type="button"
                class="btn-secondary-sm"
                @click="uploadTranscriptModalOpen = true"
              >
                <font-awesome-icon :icon="['fas', 'upload']" />Upload transcript
              </button>
            </template>
          </ComponentHeader>
          <div class="space-y-4 p-5">
            <div
              v-if="transcriptFetchMessage"
              class="rounded-lg border p-3 text-sm"
              :class="
                transcriptFetchSuccess
                  ? 'border-secondary-200 bg-secondary-100 text-green-dark'
                  : 'border-red-200 bg-red-tint text-red-dark'
              "
              role="status"
            >
              <font-awesome-icon
                :icon="[
                  'fas',
                  transcriptFetchSuccess ? 'circle-check' : 'triangle-exclamation',
                ]"
                class="mr-2"
              />{{ transcriptFetchMessage }}
            </div>

            <div
              v-if="engagement.engagement_transcription_summary"
              class="engagement-summary"
            >
              <Transcript_Summary
                :transcript-summary="engagement.engagement_transcription_summary"
                :engagement-id="engagement.id"
                :show-title="false"
                @update="updateTranscriptSummary"
              />
            </div>

            <div
              v-else-if="engagement.zoomMeetingTranscription"
              class="flex items-center gap-3"
            >
              <button
                type="button"
                class="btn-secondary-sm"
                :disabled="summarizingTranscript"
                @click="summarizeTranscript"
              >
                <font-awesome-icon
                  :icon="[
                    'fas',
                    summarizingTranscript ? 'spinner' : 'wand-magic-sparkles',
                  ]"
                  :class="{ 'fa-spin': summarizingTranscript }"
                />{{ summarizingTranscript ? 'Summarizing...' : 'Summarize transcript' }}
              </button>
              <span class="text-xs text-growth-600">Transcript attached, no summary yet.</span>
            </div>

            <p
              v-else-if="!transcriptFetchMessage"
              class="text-sm text-growth-600"
            >
              No transcript yet.
            </p>

            <div
              v-if="transcriptSummaryError"
              class="rounded-lg border border-red-light bg-red-tint p-3 text-sm text-red-dark"
              role="alert"
            >
              {{ transcriptSummaryError }}
            </div>
          </div>
        </section>

        <section class="card" :key="tag_update_key">
          <ComponentHeader title="Participants" :icon="['fas', 'users']">
            <div class="secondary-picker flex items-center gap-2">
              <button
                type="button"
                class="btn-secondary-sm"
                :disabled="addingSelf"
                @click="addSelfAsParticipant"
              >
                <font-awesome-icon :icon="['fas', addingSelf ? 'spinner' : 'user-plus']" :class="{ 'fa-spin': addingSelf }" />
                {{ addingSelf ? 'Adding…' : 'Add myself' }}
              </button>
              <EntitySearch_Single
                buttonText="Add Participant"
                modalTitle="Select Participant"
                @entity-selected="onParticipantEntitySelected"
              />
            </div>
          </ComponentHeader>
          <div v-if="engagement.engagement_participants?.length" class="overflow-x-auto">
            <table class="w-full table-fixed border-collapse text-sm">
              <colgroup>
                <col class="w-[34%]" />
                <col class="w-[26%]" />
                <col class="w-[22%]" />
                <col class="w-[12%]" />
                <col class="w-[6%]" />
              </colgroup>
              <thead>
                <tr class="text-left text-xs font-semibold text-growth-600">
                  <th class="px-4 py-2">Name</th>
                  <th class="px-3 py-2">Role</th>
                  <th class="px-3 py-2">Engagement</th>
                  <th class="px-3 py-2">Time</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="participant in engagement.engagement_participants"
                  :key="participant.id"
                  class="border-t border-growth-50 align-middle"
                >
                  <td class="sensitive break-words px-4 py-2 font-semibold text-growth-900">
                    {{ participant.name || 'Unknown' }}
                  </td>
                  <td class="break-words px-3 py-2 text-growth-800">
                    {{ participant.role || '—' }}
                  </td>
                  <td class="px-3 py-2">
                    <span class="inline-flex items-center gap-2 text-growth-800">
                      <span
                        class="h-2 w-2 rounded-full"
                        :class="getEngagementLevelClass(participant.engagement_score)"
                      ></span>
                      {{ getEngagementText(participant.engagement_score) }}
                    </span>
                  </td>
                  <td class="px-3 py-2 text-growth-800">
                    {{
                      participant.attendance_seconds
                        ? Math.round(participant.attendance_seconds / 60)
                        : 0
                    }}
                    min
                  </td>
                  <td class="pr-4 text-right">
                    <button
                      type="button"
                      class="icon-button text-growth-600 hover:text-red-dark"
                      title="Remove participant"
                      @click="openDeleteEngagementParticipantModal(participant.id)"
                    >
                      <font-awesome-icon :icon="['fas', 'trash']" class="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="px-5 py-4 text-sm text-growth-600">
            No participants recorded.
          </p>
          <div
            v-if="engagement.parsed_engagement_structure?.employee_meeting_participants?.length"
            class="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-growth-50 bg-growth-10 px-4 py-2 text-xs text-growth-600"
          >
            <span class="font-semibold">Team members</span>
            <span
              v-for="participant in engagement.parsed_engagement_structure.employee_meeting_participants"
              :key="participant.name"
              class="sensitive text-growth-800"
              >{{ participant.name }}</span
            >
          </div>
        </section>

        <section class="card">
          <ComponentHeader title="Kairon Intelligence" :icon="['fas', 'wand-magic-sparkles']">
            <router-link
              v-if="engagement.entity"
              :to="`/entity/${engagement.entity}/engagement-tracker`"
              class="inline-flex items-center gap-1.5 text-sm font-semibold text-growth-800 hover:underline"
            >
              <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" class="h-3 w-3" />Engagement Tracker
            </router-link>
            <button
              v-if="engagement.entity"
              type="button"
              class="btn-secondary-sm"
              :disabled="loading"
              @click.prevent="parseEngagement"
            >
              <font-awesome-icon
                :icon="['fas', loading ? 'spinner' : 'rotate']"
                :class="{ 'fa-spin': loading }"
              />{{ loading ? 'Processing...' : 'Refresh meeting details' }}
            </button>
            <button
              v-if="engagement.entity"
              type="button"
              class="text-sm font-semibold text-red-dark hover:underline"
              @click.prevent="deleteParsedContentModalOpen = true"
            >
              Delete parsed content
            </button>
          </ComponentHeader>
          <EngagementIntelligenceTable
            :engagement-id="engagement.id"
            :key="tag_update_key"
          />
        </section>

        <section class="card">
          <ComponentHeader title="Attachments" :icon="['fas', 'paperclip']">
            <EngagementMediaUploader
              v-if="engagement.id"
              class="attachment-upload"
              :engagement-id="engagement.id"
              @file-uploaded="onFileUploaded"
            />
          </ComponentHeader>
          <LoadingIndicator
            v-if="attachmentsLoading"
            message="Loading attachments..."
          />
          <div v-else-if="attachments.length">
            <div
              v-for="attachment in attachments"
              :key="attachment.id"
              class="flex items-center justify-between gap-4 border-t border-growth-50 px-4 py-2 first:border-t-0"
            >
              <div class="flex min-w-0 items-center gap-3">
                <font-awesome-icon
                  :icon="getFileIcon(attachment.filename)"
                  class="h-4 w-4 shrink-0 text-growth-600"
                />
                <div class="min-w-0">
                  <p class="sensitive break-words text-sm font-semibold text-growth-900">
                    {{ attachment.filename }}
                  </p>
                  <p class="text-xs text-growth-600">
                    {{ formatFileSize(attachment.file_size) }} · Uploaded
                    {{ formatDate(attachment.created_at)
                    }}<span v-if="attachment.uploaded_by_name">
                      by
                      <span class="sensitive">{{ attachment.uploaded_by_name }}</span></span
                    >
                  </p>
                </div>
              </div>
              <div class="flex shrink-0 gap-1">
                <button
                  type="button"
                  class="icon-button text-growth-600 hover:text-growth-900"
                  title="Download attachment"
                  @click="downloadAttachment(attachment)"
                >
                  <font-awesome-icon :icon="['fas', 'download']" class="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  class="icon-button text-growth-600 hover:text-red-dark"
                  title="Delete attachment"
                  @click="confirmDeleteAttachment(attachment)"
                >
                  <font-awesome-icon :icon="['fas', 'trash']" class="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
          <p v-else class="px-5 py-4 text-sm text-growth-600">
            No attachments. PDF, DOCX, PPTX or images up to 50 MB.
          </p>
        </section>

        <section class="card">
          <Action_Table
            :sql-filters="{
              originating_object_id: engagement.id,
              originating_object_content_type_model: 'engagement',
            }"
          />
        </section>
      </main>

      <transition name="fade">
        <div
          v-if="transcriptModalOpen"
          class="fixed inset-0 z-50 flex items-center justify-center bg-growth-800/60 p-5"
        >
          <div
            class="modal-surface max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-lg bg-white"
          >
            <Transcript_Modal
              :transcription_text="engagement.zoomMeetingTranscription"
              @close="transcriptModalOpen = false"
            />
          </div>
        </div>
      </transition>

      <transition name="fade">
        <div
          v-if="uploadTranscriptModalOpen"
          class="fixed inset-0 z-50 flex items-center justify-center bg-growth-800/60 p-5"
        >
          <div
            class="modal-surface max-h-[80vh] w-full max-w-3xl overflow-hidden rounded-lg bg-white"
          >
            <div class="flex items-center justify-between border-b border-growth-50 px-5 py-3">
              <h3 class="text-sm font-semibold text-growth-900">Upload transcript</h3>
              <button
                type="button"
                class="icon-button text-growth-600"
                title="Close"
                @click="closeUploadTranscriptModal"
              >
                <font-awesome-icon :icon="['fas', 'xmark']" />
              </button>
            </div>
            <div class="p-5">
              <label class="field-label">Paste transcript</label
              ><textarea
                v-model="uploadTranscriptText"
                rows="15"
                placeholder="Paste the meeting transcript here..."
                class="sensitive field-input mt-1 resize-y"
              ></textarea>
              <div
                v-if="uploadTranscriptError"
                class="mt-4 rounded-lg border border-red-light bg-red-tint p-3 text-sm text-red-dark"
              >
                {{ uploadTranscriptError }}
              </div>
            </div>
            <div class="flex justify-end gap-2 border-t border-growth-50 px-5 py-3">
              <button
                type="button"
                class="btn-secondary-sm"
                @click="closeUploadTranscriptModal"
              >
                Cancel</button
              ><button
                type="button"
                class="btn-primary-sm"
                :disabled="uploadingTranscript || !uploadTranscriptText.trim()"
                @click="submitUploadTranscript"
              >
                <font-awesome-icon
                  :icon="['fas', uploadingTranscript ? 'spinner' : 'upload']"
                  :class="{ 'fa-spin': uploadingTranscript }"
                />{{ uploadingTranscript ? 'Uploading...' : 'Upload' }}
              </button>
            </div>
          </div>
        </div>
      </transition>

      <ConfirmationModal
        :is-visible="deleteParsedContentModalOpen"
        message="Are you sure you want to delete all parsed content?"
        @confirm="delete_parsed_content"
        @cancel="deleteParsedContentModalOpen = false"
      />
      <ConfirmationModal
        :is-visible="deleteEngagementParticipantModalOpen"
        message="Are you sure you want to delete this engagement participant?"
        @confirm="deleteEngagementParticipant"
        @cancel="deleteEngagementParticipantModalOpen = false"
      />
      <ConfirmationModal
        :is-visible="deleteAttachmentModalOpen"
        message="Are you sure you want to delete this attachment?"
        @confirm="deleteAttachment"
        @cancel="deleteAttachmentModalOpen = false"
      />
      <ConfirmationModal
        :is-visible="replaceTranscriptModalOpen"
        message="This will permanently remove the current transcript, summary, and all parsed content. You can then upload a new transcript."
        @confirm="handleClearTranscript"
        @cancel="replaceTranscriptModalOpen = false"
      />
    </template>
    <LoadingIndicator v-else message="Loading engagement..." />
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import axios from 'axios';
import router from '../../../router';
import Action_Table from '@/components/Lists/Action_Table.vue';
import Transcript_Modal from '@/components/widgetcomponents/JSON_Editors/Transcript_Modal.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import EntitySearch_Single from '@/components/widgetcomponents/Search_Widgets/EntitySearch_Single.vue';
import Field_Errors from '@/components/widgetcomponents/Field_Errors.vue';
import EngagementIntelligenceTable from './EngagementIntelligenceTable.vue';
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue';
import Transcript_Summary from '@/components/widgetcomponents/JSON_Editors/Transcript_Summary.vue';
import TeamsMeetingSelector from '@/components/widgetcomponents/TeamsMeetingSelector.vue';
import SalesforceLink from '@/components/Growth/SalesforceLink.vue';
import EngagementMediaUploader from '@/components/widgetcomponents/EngagementMediaUploader.vue';
import { clearTranscript } from '@/services/api/engagements';
import EngagementTopicSelector from '@/components/widgetcomponents/EngagementTopicSelector.vue';
import PageHeader from '../widgetcomponents/library/PageHeader.vue';
import ComponentHeader from '../widgetcomponents/library/ComponentHeader.vue';
import CC_Router_Link_To_Entity from '../widgetcomponents/AG_Grid_Custom_Cells/CC_Router_Link_To_Entity.vue';
import ContextBar from '../widgetcomponents/library/ContextBar.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import { getEntityIcon } from '@/utils/entityIcons';

export default defineComponent({
  components: {
    SalesforceLink,
    TeamsMeetingSelector,
    Transcript_Summary,
    ConfirmationModal,
    EngagementIntelligenceTable,
    Field_Errors,
    EntitySearch_Single,
    FontAwesomeIcon,
    Transcript_Modal,
    Action_Table,
    EngagementMediaUploader,
    EngagementTopicSelector,
    PageHeader,
    ComponentHeader,
    CC_Router_Link_To_Entity,
    ContextBar,
    LoadingIndicator,
  },
  props: ['engagement_id'],
  data() {
    return {
      engagement: null,
      tag_update_key: 0,
      engagement_types: [],
      engagement_channels: [],
      engagement_topics: [],
      engagement_field_errors: [],
      engagement_statuses: [
        {
          name: 'Scheduled',
          id: 'SCHEDULED',
        },
        {
          name: 'Completed',
          id: 'COMPLETED',
        },
        {
          name: 'Cancelled',
          id: 'CANCELLED',
        },
        {
          name: 'Rescheduled',
          id: 'RESCHEDULED',
        },
        {
          name: 'No Show',
          id: 'NO_SHOW',
        },
      ],
      loading: false,
      autoSaveInterval: null,
      isDiscarded: false,
      transcriptModalOpen: false,
      deleteParsedContentModalOpen: false,
      replaceTranscriptModalOpen: false,
      deleteEngagementParticipantModalOpen: false,
      participantIdToDelete: null,
      lastAutoSaveTime: null,
      fetchingTranscript: false,
      transcriptFetchMessage: null,
      transcriptFetchSuccess: false,
      summarizingTranscript: false,
      transcriptSummaryError: null,
      attachments: [],
      attachmentsLoading: false,
      attachmentToDelete: null,
      deleteAttachmentModalOpen: false,
      uploadTranscriptModalOpen: false,
      uploadTranscriptText: '',
      uploadingTranscript: false,
      uploadTranscriptError: null,
      addingSelf: false,
    };
  },
  computed: {
    pageHeaderTitle() {
      if (!this.engagement) return 'Engagement Details';
      return this.engagement.entity_name
        ? `${this.formatDate(this.engagement.date)} with ${this.engagement.entity_name}`
        : `Engagement on ${this.formatDate(this.engagement.date)}`;
    },
    pageHeaderBreadcrumbs() {
      return [{ label: 'Engagement' }];
    },
    isTenantVytalize() {
      return this.$store.getters['tenant/current_tenant_slug'] === 'vytalize';
    },
    isTenantDiverge() {
      return this.$store.getters['tenant/current_tenant_slug'] === 'diverge';
    },
    entityIcon() {
      return getEntityIcon(this.engagement);
    },
    filteredEngagementChannels() {
      if (this.isTenantDiverge) {
        const allowedChannelIds = [1, 2, 3, 7];
        return this.engagement_channels.filter(channel =>
          allowedChannelIds.includes(channel.id)
        );
      }
      return this.engagement_channels;
    },
  },
  methods: {
    getEngagementLevelClass(level) {
      return (
        {
          1: 'bg-green-dark',
          0.75: 'bg-green-light',
          0.5: 'bg-yellow-dark',
          0.25: 'bg-primary-500',
          0: 'bg-red-dark',
        }[level] || 'bg-gray-400'
      );
    },
    initials(name) {
      return (
        (name || '')
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map(part => part[0].toUpperCase())
          .join('') || '—'
      );
    },
    getEngagementText(engagement_score) {
      switch (engagement_score) {
        case 1:
          return 'Very Engaged';
        case 0.75:
          return 'Engaged';
        case 0.5:
          return 'Indifferent';
        case 0.25:
          return 'Disengaged';
        case 0.0:
          return 'Hostile';
        default:
          return 'Unknown';
      }
    },
    async discardEngagement() {
      this.isDiscarded = true;
      try {
        await axios.delete(`/api/engagements/${this.engagement.id}/`);
        router.push({ name: 'Home' });
      } catch (error) {
        console.error('Error discarding engagement:', error);
      }
    },
    update() {
      this.fetchEngagementDetails();
    },
    updateEngagement() {
      this.engagement_field_errors = [];
      const updateData = {
        ...this.engagement,
      };
      delete updateData.zoomMeetingTranscription;
      delete updateData.transcription_summary;
      delete updateData.transcription_summary_status;
      delete updateData.parsed_engagement_structure;
      delete updateData.engagement_topic_names;

      return axios
        .patch(`/api/engagements/${this.engagement.id}/`, updateData)
        .then(response => {
          this.engagement = response.data;
          this.lastAutoSaveTime = new Date().toLocaleTimeString();

          // Handle transcript summary completion
          if (this.summarizingTranscript) {
            if (this.engagement.transcription_summary_status === 'COMPLETED') {
              this.summarizingTranscript = false;
              this.transcriptSummaryError = null;
            } else if (
              this.engagement.transcription_summary_status === 'FAILED'
            ) {
              this.summarizingTranscript = false;
              this.transcriptSummaryError = 'Failed to summarize transcript';
            }
          }
        })
        .catch(error => {
          console.error('Error updating engagement:', error);
          this.engagement_field_errors = [
            `Error updating engagement, please email support@kaironhealth.com: ${error}`,
          ];
        });
    },
    updateTranscriptSummary(new_summary) {
      const updateData = {
        transcription_summary: JSON.stringify(new_summary),
      };

      return axios
        .patch(`/api/engagements/${this.engagement.id}/`, updateData)
        .then(response => {
          this.engagement = response.data;
        })
        .catch(error => {
          console.error('Error updating engagement:', error);
          this.engagement_field_errors = [
            `Error updating engagement, please email support@kaironhealth.com: ${error}`,
          ];
        });
    },
    fetchEngagementChannels() {
      axios
        .get('/api/engagementChannels/')
        .then(response => {
          this.engagement_channels = response.data;
        })
        .catch(error => {
          console.error('Error fetching engagement types: ', error);
        });
    },
    fetchEngagementTypes() {
      axios
        .get('/api/engagementtypes/')
        .then(response => {
          this.engagement_types = response.data;
        })
        .catch(error => {
          console.error('Error fetching engagement types: ', error);
        });
    },
    fetchEngagementTopics() {
      axios
        .get('/api/engagementtopics/')
        .then(response => {
          this.engagement_topics = response.data;
        })
        .catch(error => {
          console.error('Error fetching engagement topics: ', error);
        });
    },
    onParticipantEntitySelected(entity) {
      axios
        .post('api/engagementparticipants/', {
          engagement: this.engagement.id,
          participant: entity.id,
          attendance_seconds: 0,
          engagement_score: null,
        })
        .then(() => {
          this.fetchEngagementDetails();
        })
        .catch(error => {
          console.error('Error adding engagement participant:', error);
        });
    },
    async addSelfAsParticipant() {
      if (!this.engagement?.id || this.addingSelf) return;
      this.addingSelf = true;
      try {
        await axios.post('/api/engagementparticipants/add-self/', {
          engagement: this.engagement.id,
        });
        await this.fetchEngagementDetails();
      } catch (error) {
        console.error('Error adding self as participant:', error);
      } finally {
        this.addingSelf = false;
      }
    },
    parseEngagement() {
      if (
        this.engagement.entity &&
        (this.engagement?.note?.length > 20 ||
          this.engagement?.zoomMeetingTranscription?.length > 20)
      ) {
        this.loading = true;
        axios
          .post(`/api/engagements/${this.engagement.id}/parse/`)
          .then(() => {
            this.fetchEngagementDetails();
            this.$emit('update');
          })
          .catch(error => {
            console.error('Error parsing engagements', error);
          })
          .finally(() => {
            this.tag_update_key += 1;
            this.loading = false;
          });
      }
    },
    delete_parsed_content() {
      if (this.engagement_id) {
        this.loading = true;
        axios
          .post(`/api/engagements/${this.engagement.id}/delete_parsed_content/`)
          .then(() => {
            this.$emit('update');
          })
          .catch(error => {
            console.error('Error parsing engagements', error);
          })
          .finally(() => {
            this.tag_update_key += 1;
            this.deleteParsedContentModalOpen = false;
            this.engagement.parsed_engagement_structure = null;
            this.loading = false;
          });
      }
    },
    fetchEngagementDetails() {
      return axios
        .get(`/api/engagements/${this.engagement_id}/`)
        .then(response => {
          this.engagement = response.data;
        })
        .catch(error => {
          const { status } = error.response;
          if (status === 403) {
            router.push({ name: 'ForbiddenPage' });
          } else if (status === 404) {
            router.push({ name: 'NotFound' });
          } else if (status >= 500) {
            router.push({ name: 'ErrorPage' });
          }
          console.error('Error fetching engagement:', error);
        });
    },
    onEntitySelected(entity) {
      this.engagement.entity = entity.id;
      this.engagement.entity_name = entity.name;
    },
    deleteEngagementParticipant() {
      axios
        .delete(`/api/engagementparticipants/${this.participantIdToDelete}/`)
        .then(() => {
          this.fetchEngagementDetails();
          this.deleteEngagementParticipantModalOpen = false;
        })
        .catch(error => {
          console.error('Error deleting engagement participant:', error);
        });
    },
    openDeleteEngagementParticipantModal(participantId) {
      this.deleteEngagementParticipantModalOpen = true;
      this.participantIdToDelete = participantId;
    },
    fetchTranscript() {
      this.fetchingTranscript = true;
      this.transcriptFetchMessage = null;
      this.transcriptFetchSuccess = false;
      axios
        .get(`/api/engagements/${this.engagement_id}/transcript/`)
        .then(() => {
          this.fetchEngagementDetails();
          this.transcriptFetchMessage =
            'Transcript search completed successfully. Press Parse Content to analyze.';
          this.transcriptFetchSuccess = true;
        })
        .catch(() => {
          this.transcriptFetchMessage = 'No transcript found.';
          this.transcriptFetchSuccess = false;
        })
        .finally(() => {
          this.fetchingTranscript = false;
        });
    },
    summarizeTranscript() {
      this.summarizingTranscript = true;
      this.transcriptSummaryError = null;
      axios
        .post(`/api/engagements/${this.engagement_id}/summarize_transcript/`)
        .catch(error => {
          console.error('Error summarizing transcript:', error);
          this.transcriptSummaryError =
            error.response?.data?.message || error.message;
          this.summarizingTranscript = false;
        });
    },
    // Attachment methods
    async fetchAttachments() {
      if (!this.engagement_id) return;
      this.attachmentsLoading = true;
      try {
        const response = await axios.get('/api/engagementmedia/', {
          params: { engagement: this.engagement_id },
        });
        this.attachments = response.data;
      } catch (error) {
        console.error('Error fetching attachments:', error);
      } finally {
        this.attachmentsLoading = false;
      }
    },
    onFileUploaded(fileData) {
      this.fetchAttachments();
    },
    async downloadAttachment(attachment) {
      try {
        const response = await axios.get(
          `/api/engagementmedia/${attachment.id}/download/`
        );
        if (response.data.url) {
          window.open(response.data.url, '_blank', 'noopener');
        }
      } catch (error) {
        console.error('Error downloading attachment:', error);
      }
    },
    confirmDeleteAttachment(attachment) {
      this.attachmentToDelete = attachment;
      this.deleteAttachmentModalOpen = true;
    },
    async deleteAttachment() {
      if (!this.attachmentToDelete) return;
      try {
        await axios.delete(
          `/api/engagementmedia/${this.attachmentToDelete.id}/`
        );
        this.fetchAttachments();
      } catch (error) {
        console.error('Error deleting attachment:', error);
      } finally {
        this.attachmentToDelete = null;
        this.deleteAttachmentModalOpen = false;
      }
    },
    async handleClearTranscript() {
      this.replaceTranscriptModalOpen = false;
      try {
        await clearTranscript(this.engagement.id);
        await this.fetchEngagementDetails();
      } catch (error) {
        console.error('Error clearing transcript:', error);
      }
    },
    getFileIcon(filename) {
      const ext = filename?.split('.').pop()?.toLowerCase();
      if (['doc', 'docx'].includes(ext)) return ['fas', 'file-word'];
      if (ext === 'pdf') return ['fas', 'file-pdf'];
      if (['ppt', 'pptx'].includes(ext)) return ['fas', 'file-powerpoint'];
      if (['jpg', 'jpeg', 'png'].includes(ext)) return ['fas', 'file-image'];
      return ['fas', 'file'];
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const safe = /^\d{4}-\d{2}-\d{2}$/.test(dateString) ? dateString + 'T12:00:00' : dateString;
      const date = new Date(safe);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },
    closeUploadTranscriptModal() {
      this.uploadTranscriptModalOpen = false;
      this.uploadTranscriptText = '';
      this.uploadTranscriptError = null;
    },
    async submitUploadTranscript() {
      if (!this.uploadTranscriptText.trim()) {
        this.uploadTranscriptError =
          'Please paste a transcript before uploading.';
        return;
      }

      this.uploadingTranscript = true;
      this.uploadTranscriptError = null;

      try {
        await axios.patch(`/api/engagements/${this.engagement_id}/`, {
          zoomMeetingTranscription: this.uploadTranscriptText.trim(),
        });

        // Refresh engagement data
        await this.fetchEngagementDetails();

        // Close modal and reset
        this.closeUploadTranscriptModal();

        // Auto-trigger transcript summarization
        this.summarizeTranscript();
      } catch (error) {
        console.error('Error uploading transcript:', error);
        this.uploadTranscriptError =
          error.response?.data?.message ||
          'Failed to upload transcript. Please try again.';
      } finally {
        this.uploadingTranscript = false;
      }
    },
  },
  mounted() {
    this.fetchEngagementDetails();
    this.fetchEngagementChannels();
    this.fetchEngagementTypes();
    this.fetchEngagementTopics();
    this.fetchAttachments();
    this.autoSaveInterval = setInterval(this.updateEngagement, 30000);
  },
  beforeUnmount() {
    clearInterval(this.autoSaveInterval);
    if (!this.isDiscarded) {
      this.updateEngagement();
    }
  },
  watch: {},
});
</script>

<style scoped>
@reference '../../assets/theme.css';

.engagement-page-header :deep(> div) {
  padding: 20px 40px;
}

@media (max-width: 767px) {
  .engagement-page-header :deep(> div) {
    padding-left: 20px;
    padding-right: 20px;
  }
}

.card {
  @apply overflow-hidden rounded-lg border border-growth-50 bg-white;
}

.field-label {
  @apply block text-xs font-semibold text-gray-700;
}

.field-input {
  @apply w-full rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-growth-800 focus:border-blue-dark focus:outline-none focus:ring-2 focus:ring-blue-dark;
}

.icon-button {
  @apply inline-flex h-7 w-7 items-center justify-center rounded focus:outline-none focus:ring-2 focus:ring-blue-dark;
}

.modal-surface {
  box-shadow: 0 20px 100px rgba(0, 0, 0, 0.2);
}

.secondary-picker :deep(.btn-primary),
.attachment-upload :deep(.btn-primary-sm) {
  @apply px-2 py-1.5 text-sm bg-white border border-neutral-700 text-neutral-700 font-semibold rounded flex items-center justify-center gap-2 hover:text-neutral-600 hover:bg-white;
}

.partner-box :deep(.btn-primary) {
  @apply border-0 bg-transparent p-0 text-xs font-semibold text-neutral-700 hover:bg-transparent hover:text-neutral-900;
}

.topic-selector :deep(label) {
  @apply text-xs font-semibold text-gray-700 mb-1;
}

.topic-selector :deep(button.w-full) {
  @apply px-3 py-1.5 text-sm;
}

.topic-selector :deep(button.w-full > span) {
  @apply text-sm text-growth-800;
}

.topic-selector :deep(.rounded-full) {
  @apply text-xs;
}

.engagement-summary :deep(.bg-blue-100) {
  background: transparent;
  border: 0;
  padding: 0 16px;
  margin-top: 0;
}

.fa-spin {
  animation: fa-spin 2s infinite linear;
}

@keyframes fa-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
